import React, { useState } from 'react'
import { Box, IconButton, Popover } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { useThemeAccent } from '../../contexts/ThemeAccentContext'
import { buildKiotHeaderIconButtonSx } from './muahangKiotTokens'
import { uiPalette } from '../../theme/uiPalette'

type ThemeAccentPickerProps = {
  /** `header`: nút trên navbar. `surface`: nút trên nền login. */
  variant?: 'header' | 'surface'
}

const ThemeAccentPicker: React.FC<ThemeAccentPickerProps> = ({ variant = 'header' }) => {
  const theme = useTheme()
  const { accentId, setAccentId, presets } = useThemeAccent()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const isSurface = variant === 'surface'
  const surfaceBorder = theme.palette.muahang?.primarySurfaceBorder ?? uiPalette.primary.surfaceBorder
  const surfaceBg = theme.palette.muahang?.primarySurface ?? uiPalette.primary.surface

  const headerIconSx = {
    ...buildKiotHeaderIconButtonSx({
      activeBg: theme.palette.muahang.navItemActiveBg,
      activeBgHover: theme.palette.muahang.navItemActiveBgHover,
    }),
    height: '100%',
    minHeight: 0,
    alignSelf: 'stretch',
  }

  return (
    <>
      <IconButton
        color="inherit"
        size={isSurface ? 'medium' : 'small'}
        aria-label="Chọn màu chủ đề giao diện"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={
          isSurface
            ? {
                color: theme.palette.primary.main,
                border: `1px solid ${surfaceBorder}`,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
                '&:hover': {
                  backgroundColor: surfaceBg,
                },
              }
            : headerIconSx
        }
      >
        <PaletteOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              mt: 0.25,
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.25,
            width: 132,
          }}
        >
          {presets.map((p) => {
            const selected = p.id === accentId
            return (
              <Box
                key={p.id}
                component="button"
                type="button"
                aria-label={p.id}
                aria-pressed={selected}
                onClick={() => {
                  setAccentId(p.id)
                  setAnchorEl(null)
                }}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '6px',
                  border: selected ? '2px solid rgba(0,0,0,0.45)' : '2px solid rgba(0,0,0,0.12)',
                  bgcolor: p.main,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0,
                  m: 0,
                  boxSizing: 'border-box',
                  transition: 'transform 80ms ease, border-color 120ms ease',
                  '&:hover': { transform: 'scale(1.04)' },
                }}
              >
                {selected ? (
                  <CheckIcon
                    sx={{
                      color: '#fff',
                      fontSize: 22,
                      filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.55))',
                    }}
                  />
                ) : null}
              </Box>
            )
          })}
        </Box>
      </Popover>
    </>
  )
}

export default ThemeAccentPicker
